import { gql } from '@apollo/client';
import type { ListOrderPoolPageVariables } from './routePlanningOrderPool';

/** 选择模式：勾选若干条 / 按当前筛选跨页全选 */
export type HoldingSelectionMode = 'BY_IDS' | 'BY_FILTER';

/**
 * 暂存状态。10-暂存中是唯一可以移回的状态；20/30/40 是终态，记录保留可见但不允许再移回。
 * 15-移回中是中间态，后台异步处理中。
 */
export const HOLD_STATUS = {
  HOLDING: 10,
  RESTORING: 15,
  RESTORED: 20,
  RESTORE_FAILED: 30,
  INVALIDATED: 40,
} as const;

/** 状态码 → i18n key 后缀，用于列表展示与筛选下拉 */
export const HOLD_STATUS_I18N_KEY: Record<number, string> = {
  [HOLD_STATUS.HOLDING]: 'HOLDING',
  [HOLD_STATUS.RESTORING]: 'RESTORING',
  [HOLD_STATUS.RESTORED]: 'RESTORED',
  [HOLD_STATUS.RESTORE_FAILED]: 'RESTORE_FAILED',
  [HOLD_STATUS.INVALIDATED]: 'INVALIDATED',
};

/** 可以点「移回」的状态：行级勾选只放开 10，其余一律禁用 */
export const RESTORABLE_HOLD_STATUS: readonly number[] = [HOLD_STATUS.HOLDING];

/**
 * 表头「全选」（跨页按筛选下发）允许出现的状态。
 * 与后端 `OrderPoolHoldingCommandServiceImpl.RESTORE_QUERY_STATUS` 对齐：
 * 15-移回中在后台卡住超过阈值后可重新提交，「筛选移回中 + 全选」是该恢复路径在页面上的唯一入口。
 */
export const SELECT_ALL_HOLD_STATUS: readonly number[] = [HOLD_STATUS.HOLDING, HOLD_STATUS.RESTORING];

/** 受理结果码，与后端 BatchAcceptedDTO.RESULT_* 一一对应 */
export const BATCH_RESULT = {
  ACCEPTED: 'ACCEPTED',
  EXCEED_LIMIT: 'EXCEED_LIMIT',
  GRAY_NOT_HIT: 'GRAY_NOT_HIT',
  NO_MATCH: 'NO_MATCH',
  REJECTED: 'REJECTED',
} as const;

/** 灰度查询：仓 + 当前登录账号需同时命中（accountId 由后端注入，前端不传） */
export interface IsOrderPoolHoldingGrayResponse {
  isOrderPoolHoldingGray: boolean;
}

export interface IsOrderPoolHoldingGrayVariables {
  warehouseId: number;
}

export const IS_ORDER_POOL_HOLDING_GRAY = gql`
  query IsOrderPoolHoldingGray($warehouseId: Long!) {
    isOrderPoolHoldingGray(warehouseId: $warehouseId)
  }
`;

export interface OrderPoolHoldingPageItem {
  orderId: number | null;
  trackingNumber: string | null;
  overallStatus: number | null;
  areaName: string | null;
  bagNumber: string | null;
  dspName: string | null;
  hubArrivalTime: string | null;
  whsArrivalTime: string | null;
  heldAt: string | null;
  heldBy: string | null;
  holdRemark: string | null;
  holdStatus: number | null;
  failReason: string | null;
}

export interface ListOrderPoolHoldingPageResult {
  content: OrderPoolHoldingPageItem[];
  total: number;
  pageNum: number;
  pageSize: number;
}

export interface ListOrderPoolHoldingPageResponse {
  listOrderPoolHoldingPage: ListOrderPoolHoldingPageResult;
}

/** 暂存列表筛选项与规划运单池一致（无 parcelType / dspId：暂存页不分子页签、不给 DSP） */
export interface OrderPoolHoldingFilter {
  warehouseId: number;
  trackingNumbers?: string[] | null;
  bagNumbers?: string[] | null;
  hubArrivalTimeStart?: string | null;
  hubArrivalTimeEnd?: string | null;
  whsArrivalTimeStart?: string | null;
  whsArrivalTimeEnd?: string | null;
  /** 单区域筛选，已被 areaIds 取代；后端保留该字段用于兼容旧前端 */
  areaId?: number | null;
  /** 多区域筛选（取并集）；有值时后端优先用它 */
  areaIds?: number[] | null;
  overallStatus?: number | null;
  /** 暂存状态筛选；不传时后端默认只返回 10-暂存中 */
  holdStatusList?: number[] | null;
  pageNum?: number | null;
  pageSize?: number | null;
}

export interface ListOrderPoolHoldingPageVariables {
  request: OrderPoolHoldingFilter;
}

export const LIST_ORDER_POOL_HOLDING_PAGE = gql`
  query ListOrderPoolHoldingPage($request: ListOrderPoolHoldingPageRequestInput!) {
    listOrderPoolHoldingPage(request: $request) {
      content {
        orderId
        trackingNumber
        overallStatus
        areaName
        bagNumber
        dspName
        hubArrivalTime
        whsArrivalTime
        heldAt
        heldBy
        holdRemark
        holdStatus
        failReason
      }
      total
      pageNum
      pageSize
    }
  }
`;

/** 暂存 / 移回的受理结果：真正的出池、入池在后端异步完成 */
export interface BatchAccepted {
  resultCode: string | null;
  batchId: string | null;
  matchedCount: number;
  acceptedCount: number;
  ignoredCount: number;
  maxLimit: number;
}

export interface HoldOrderPoolResponse {
  holdOrderPool: BatchAccepted;
}

export interface HoldOrderPoolVariables {
  request: {
    warehouseId: number;
    selectionMode?: HoldingSelectionMode | null;
    orderIds?: number[] | null;
    /** BY_FILTER 时传规划运单池列表的同款筛选（分页被后端忽略） */
    filter?: ListOrderPoolPageVariables['request'] | null;
    remark?: string | null;
  };
}

export const HOLD_ORDER_POOL = gql`
  mutation HoldOrderPool($request: HoldOrderPoolRequestInput!) {
    holdOrderPool(request: $request) {
      resultCode
      batchId
      matchedCount
      acceptedCount
      ignoredCount
      maxLimit
    }
  }
`;

export interface RestoreOrderPoolResponse {
  restoreOrderPool: BatchAccepted;
}

export interface RestoreOrderPoolVariables {
  request: {
    warehouseId: number;
    selectionMode?: HoldingSelectionMode | null;
    orderIds?: number[] | null;
    /** BY_FILTER 时传暂存列表的同款筛选（分页被后端忽略） */
    filter?: OrderPoolHoldingFilter | null;
  };
}

export const RESTORE_ORDER_POOL = gql`
  mutation RestoreOrderPool($request: RestoreOrderPoolRequestInput!) {
    restoreOrderPool(request: $request) {
      resultCode
      batchId
      matchedCount
      acceptedCount
      ignoredCount
      maxLimit
    }
  }
`;
